import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    Tooltip,
    IconButton,
    Collapse
} from '@mui/material';
import {
    AccessTime,
    Security,
    ExpandMore,
    ExpandLess
} from '@mui/icons-material';
import { useAuth } from './AuthContext';

const SessionStatus = ({ compact = false }) => {
    const { 
        isAuthenticated, 
        lastActivity, 
        SESSION_TIMEOUT, 
        getRemainingSessionTime,
        sessionWarning 
    } = useAuth();
    
    const [expanded, setExpanded] = useState(false);
    const [currentTime, setCurrentTime] = useState(Date.now());

    // Update current time every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    if (!isAuthenticated) {
        return null;
    }

    const formatTimeAgo = (timestamp) => {
        const minutes = Math.floor((Date.now() - timestamp) / (1000 * 60));
        if (minutes < 1) return 'Just now';
        if (minutes === 1) return '1 minute ago';
        return `${minutes} minutes ago`;
    };

    const remainingTime = getRemainingSessionTime ? getRemainingSessionTime() : SESSION_TIMEOUT;
    const isNearExpiry = remainingTime <= 5;

    if (compact) {
        return (
            <Tooltip title={`Session expires in ${remainingTime} minutes`}>
                <Chip
                    icon={<AccessTime />}
                    label={`${remainingTime}m`}
                    color={isNearExpiry ? 'warning' : 'default'}
                    size="small"
                    variant={sessionWarning ? 'filled' : 'outlined'}
                />
            </Tooltip>
        );
    }

    return (
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security color="primary" />
                    <Typography variant="subtitle2">
                        Session Status
                    </Typography>
                    <Chip
                        label={`${remainingTime}m remaining`}
                        color={isNearExpiry ? 'warning' : 'success'}
                        size="small"
                    />
                </Box>
                <IconButton 
                    size="small" 
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>
            
            <Collapse in={expanded}>
                <Box sx={{ mt: 2, pl: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                        Last activity: {formatTimeAgo(lastActivity)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Session timeout: {SESSION_TIMEOUT} minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Auto-refresh: Active
                    </Typography>
                    {sessionWarning && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                            ⚠️ Session warning active
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default SessionStatus;
